import { ComponentPropsWithoutRef, forwardRef, useImperativeHandle, useState, useEffect, useRef } from 'react';
import { CurrencyInput } from './CurrencyInput';
import { percentageStringToNumber } from '../../../../util';
import { InputProps } from './Input';
import { InputContainer } from './components';
import { elementsInputClasses } from './util';

export interface PercentageInputProps extends Omit<InputProps, 'inputProps' | 'onChange'> {
  value?: number | null;
  decimalsLimit?: number;
  placeholder?: string;
  onChange?: (value: number | null) => void;
  inputProps?: Omit<ComponentPropsWithoutRef<'input'>, 'onClick' | 'name' | 'disabled' | 'onChange' | 'onBlur' | 'id'>;
  allowNegativeValue?: boolean;
}

export const PercentageInput = forwardRef<HTMLInputElement, PercentageInputProps>(
  (
    {
      name,
      value,
      decimalsLimit = 2,
      onChange,
      inputProps,
      placeholder,
      allowNegativeValue,
      ...inputContainerProps
    },
    forwardRef
  ) => {
    const [localInput, setLocalInput] = useState<string | null>(value?.toString() || null);

    const ref = useRef<HTMLInputElement>(null);
    useImperativeHandle(forwardRef, () => ref.current as HTMLInputElement);

    useEffect(() => {
      if (localInput === null || localInput === undefined) {
        setLocalInput(value?.toString() || null);
      }
    }, [value]);

    return (
      <InputContainer isReadOnly={!!inputProps?.readOnly} {...inputContainerProps}>
        <CurrencyInput
          allowNegativeValue={allowNegativeValue}
          ref={ref}
          id={name}
          value={localInput ?? undefined}
          suffix="%"
          name={name}
          placeholder={placeholder}
          decimalsLimit={decimalsLimit}
          maxLength={3 + decimalsLimit}
          defaultValue={localInput || undefined}
          className={elementsInputClasses.input}
          onValueChange={(value, _, values) => {
            setLocalInput(value || null);
            onChange?.(values?.float || null);
            if (value?.endsWith('%')) {
              ref.current?.setSelectionRange(value.length - 1, value.length - 1);
            }
          }}
          transformRawValue={(v) => {
            const num = percentageStringToNumber(v);
            if (num === 100 || (num > 100 && !v.includes('.'))) {
              return '100';
            }
            return v;
          }}
        />
      </InputContainer>
    );
  }
);
